import { ILocationResponse } from '@kcftech/prometheustypescriptapiclient';
import { useMutation } from '@tanstack/react-query';
import { TextInlineEditor } from 'components/inline_edit/TextInlineEditor';
import { useNotificationContext } from 'contexts/NotificationContext';
import { usePermissions } from 'contexts/PermissionsContext';
import { infiniteLocationsDataSourceKey } from 'custom_hooks/data_hooks/data_source/locationsInfiniteDataSource';
import { invalidateLocation } from 'custom_hooks/data_hooks/location/useLocation';
import { maxLocationNameLength } from 'pages/Settings/Locations/shared';
import { updateLocation } from 'utils/location';
import { queryClient } from 'utils/query';

export type LocationNameInlineEditorProps = Readonly<{
    location: ILocationResponse;
    onChange: () => void;
    className?: string;
}>;

export function LocationNameInlineEditor({
    location,
    onChange,
    ...rest
}: LocationNameInlineEditorProps) {
    const { canEditLocationDetails } = usePermissions();
    const { showToast } = useNotificationContext();
    const { mutate: updateName, isPending } = useMutation({
        mutationFn: (name: string) => updateLocation(location, { Name: name }),

        onSuccess: () => {
            onChange();
            invalidateLocation(location.Id);
            queryClient.invalidateQueries({
                queryKey: [infiniteLocationsDataSourceKey],
            });
            showToast({
                type: 'success',
                content: 'Location name saved',
            });
        },

        onError: () =>
            showToast({
                type: 'error',
                content: 'Failed to save location name',
            }),
    });

    return (
        <TextInlineEditor
            value={location.Name}
            isDisabled={isPending || !canEditLocationDetails}
            onConfirm={(val) => updateName(val ?? '')}
            validate={(val) => {
                if (!val) {
                    return 'Location name is required';
                }
                if (val.length > maxLocationNameLength) {
                    return `Location name must be ${maxLocationNameLength} characters or less`;
                }
                return undefined;
            }}
            {...rest}
        />
    );
}
